import { useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useInView } from 'framer-motion';
import { Services } from './Services';
import { fadeInUp } from '../../lib/animations';
import { staggerContainer } from '../../lib/animations';
import { useLanguage } from '../../context/LanguageContext';

export const Facilities = () => {
  const { t } = useLanguage();
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const facilities = [
    {
      title: t.facilities.courts,
      description: t.facilities.courtsDesc,
      image: 'https://customer-assets-rejwkqb3.emergentagent.net/job_padel-arena-modern/artifacts/gifcfzdk_DSC06849.webp',
      span: 'md:col-span-8',
      link: '/courts'
    },
    {
      title: t.facilities.golf,
      description: t.facilities.golfDesc,
      image: 'https://customer-assets.emergentagent.com/job_padel-arena-modern/artifacts/uvwu077t_DSC06253.jpg',
      span: 'md:col-span-4',
      link: '/golf'
    },
    {
      title: t.facilities.bar,
      description: t.facilities.barDesc,
      image: 'https://customer-assets.emergentagent.com/job_padel-arena-modern/artifacts/vqamn5j5_DSC07851.jpg',
      span: 'md:col-span-8',
      link: '/sports-bar'
    },
    {
      title: t.facilities.shop,
      description: t.facilities.shopDesc,
      image: 'https://customer-assets.emergentagent.com/job_padel-arena-modern/artifacts/ru4ttb39_DSC06257.jpg',
      span: 'md:col-span-4',
      link: '/pro-shop'
    }
  ];

  return (
    <section id="facilities" ref={ref} className="py-32 bg-slate-50" data-testid="facilities-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="max-w-3xl mb-20"
        >
          <p className="overline mb-4">{t.facilities.showcaseLabel}</p>
          <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-slate-900 mb-6 tracking-tight">
            {t.facilities.title}
          </h2>
          <p className="text-xl text-slate-600">
            {t.facilities.subtitle}
          </p>
        </motion.div>

        <motion.div 
          variants={staggerContainer}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          className="bento-grid"
        >
          {facilities.map((facility, index) => {
            const inner = (
              <div className="relative h-80 overflow-hidden">
                <div 
                  className="absolute inset-0 bg-cover bg-center transition-transform duration-700 group-hover:scale-110"
                  style={{ backgroundImage: `url(${facility.image})` }}
                >
                  <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-transparent"></div>
                </div>
                {facility.link && (
                  <div className="absolute top-4 right-4 px-3 py-1.5 bg-white/90 backdrop-blur-sm rounded-full text-[10px] font-bold uppercase tracking-wider text-slate-900 opacity-0 group-hover:opacity-100 transition-opacity">
                    {t.facilities.exploreCta} →
                  </div>
                )}
                <div className="absolute bottom-0 left-0 right-0 p-8 text-white">
                  <h3 className="text-2xl md:text-3xl font-bold mb-3">{facility.title}</h3>
                  <p className="text-slate-200 text-sm md:text-base">{facility.description}</p>
                </div>
              </div>
            );
            const baseCls = `${facility.span} relative group overflow-hidden rounded-3xl minimal-card`;
            return facility.link ? (
              <motion.div key={index} variants={fadeInUp} className={baseCls}>
                <Link to={facility.link} className="block" data-testid={`facility-link-${facility.link.replace('/','')}`}>
                  {inner}
                </Link>
              </motion.div>
            ) : (
              <motion.div key={index} variants={fadeInUp} className={baseCls}>
                {inner}
              </motion.div>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
};

// Services Section
